// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Messages + Settings Pages

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeToQuery, callFunction, where, orderBy, limit, AppError,
  db, doc, updateDoc }
  from '../../services/firebase';
import { AdminListSection } from '../../components/AdminListSection';

// ─────────────────────────────────────────────────────────────────────────────
// MESSAGES PAGE
// ─────────────────────────────────────────────────────────────────────────────

interface MessageDoc {
  id: string;
  orgId: string;
  subject?: string;
  body: string;
  readBy: Record<string, any>;
  publishedAt: any;
  status: string;
  isDeleted?: boolean;
}

interface SubmissionDoc {
  id: string;
  contentData?: { subject?: string; body?: string; deliveryMode?: 'immediate' | 'batch';
                  sharedWithOrg?: boolean };
  status: string;
  createdAt: any;
}

// Submissions can only be edited or withdrawn while review is still in
// progress — once a submission reaches 'pending_post' it's "Approved · queued
// for delivery" and can no longer be changed here, only revoked once live.
const isPreReview = (status: string) => status !== 'pending_post';

// content_submissions statuses worth surfacing to org admins, with a friendly
// label/badge — 'posted' graduates to the live `messages` list below, and
// 'blocked_csam' routes to the legal/CLO escalation pipeline, not org admins.
const submissionStatusMeta: Record<string, { label: string; badge: string }> = {
  pending_ai:         { label: 'Submitted · scanning',           badge: 'badge-gray' },
  pending_post:       { label: 'Cleared · posts within 1 hour',  badge: 'badge-blue' },
  held_flagged:       { label: 'Held for review',                badge: 'badge-amber' },
  pending_clo_review: { label: 'Held for review',                badge: 'badge-amber' },
  held_scan_error:    { label: 'Held for review',                badge: 'badge-amber' },
};

export function MessagesPage() {
  const { activeOrgId, activeOrg, user, isPlatformAdmin } = useAuth();
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [pendingSubmissions, setPendingSubmissions] = useState<SubmissionDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [compose, setCompose] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<'immediate' | 'batch'>('batch');
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeOrgId || !user) return;
    const constraints = isPlatformAdmin
      ? [where('orgId', '==', activeOrgId), where('sharedWithOrg', '==', true), limit(200)]
      : [where(`memberUidMap.${user.uid}`, '==', true), limit(200)];
    return subscribeToQuery<MessageDoc>(
      'messages',
      constraints,
      data => {
        setMessages(
          data
            .filter(m => !m.isDeleted && (isPlatformAdmin || m.orgId === activeOrgId))
            .sort((a, b) => (b.publishedAt?.seconds ?? 0) - (a.publishedAt?.seconds ?? 0))
        );
        setIsLoading(false);
      },
      err => { console.error('[messages subscription]', err); setIsLoading(false); }
    );
  }, [activeOrgId, user?.uid, isPlatformAdmin]);

  // Submissions still moving through the moderation pipeline — not yet live
  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToQuery<SubmissionDoc>(
      'content_submissions',
      [where('orgId', '==', activeOrgId), where('contentType', '==', 'message'), limit(20)],
      data => setPendingSubmissions(
        data
          .filter(s => submissionStatusMeta[s.status])
          .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
      ),
      err => console.error('[messages pending subscription]', err)
    );
  }, [activeOrgId]);

  const closeCompose = () => {
    setCompose(false); setEditingSubmissionId(null);
    setSubject(''); setBody(''); setDeliveryMode('batch'); setError(null);
  };

  const handleSend = async () => {
    if (!body.trim()) { setError('Message body is required.'); return; }
    setIsSubmitting(true); setError(null);
    try {
      const contentData = { subject, body, sharedWithOrg: true, deliveryMode };
      if (editingSubmissionId) {
        await callFunction('editSubmission', { submissionId: editingSubmissionId, contentData });
      } else {
        await callFunction('submitContent', {
          orgId: activeOrgId,
          contentType: 'message',
          isUserGenerated: false,
          contentData,
        });
      }
      closeCompose();
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 8000);
    } catch (err) {
      setError((err as AppError).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmission = (sub: SubmissionDoc) => {
    setSubject(sub.contentData?.subject ?? '');
    setBody(sub.contentData?.body ?? '');
    setDeliveryMode(sub.contentData?.deliveryMode ?? 'batch');
    setEditingSubmissionId(sub.id);
    setError(null); setActionError(null);
    setCompose(true);
  };

  const handleCancelSubmission = async (submissionId: string) => {
    setActionError(null);
    try {
      await callFunction('cancelSubmission', { submissionId });
      if (editingSubmissionId === submissionId) closeCompose();
    } catch (err) {
      setActionError((err as AppError).message);
    }
  };

  const isRead = (msg: MessageDoc) => !!(user && msg.readBy?.[user.uid]);

  if (!activeOrgId) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
      <p style={{ fontSize: 15, marginBottom: 8 }}>No org selected</p>
      <p style={{ fontSize: 13 }}>Use the org picker in the top nav to select an org, then return here to view and manage its messages.</p>
    </div>
  );

  return (
    <div>
      {submitSuccess && (
        <div className="banner banner-success" style={{ marginBottom: 12 }}>
          Your message has been submitted for review. It will appear to members within a few minutes.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Messages — {activeOrg?.name}</h1>
        <button className="btn btn-primary" onClick={() => (compose ? closeCompose() : setCompose(true))}>
          {compose ? 'Cancel' : '+ Compose'}
        </button>
      </div>

      {actionError && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {actionError}
          <button onClick={() => setActionError(null)} style={{ marginLeft: 'auto',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-danger)', fontSize: 16 }}>×</button>
        </div>
      )}

      {compose && (
        <div className="card" style={{ marginBottom: 16, padding: 18 }}>
          <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>
            {editingSubmissionId ? 'Edit Message' : 'New Message'}
          </h2>
          {error && <div className="error-banner" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label" htmlFor="msg-subject">Subject (optional)</label>
            <input id="msg-subject" type="text" className="form-input" value={subject}
              onChange={e => setSubject(e.target.value)} placeholder="Announcement" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="msg-body">Message</label>
            <textarea id="msg-body" className="form-textarea" value={body}
              onChange={e => setBody(e.target.value)} rows={4}
              placeholder="Write your message… Markdown supported." required />
          </div>
          <div style={{ display: 'flex', alignItems: 'center',
            gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Delivery:
            </span>
            <div className="flavor-tabs">
              <button className={`ftab${deliveryMode === 'batch' ? ' active' : ''}`}
                onClick={() => setDeliveryMode('batch')}>Batch (Nightly)</button>
              <button className={`ftab${deliveryMode === 'immediate' ? ' active' : ''}`}
                onClick={() => setDeliveryMode('immediate')}>Immediate</button>
            </div>
            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
              {deliveryMode === 'batch'
                ? 'Delivers tonight at midnight ET'
                : 'Delivers immediately after review'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSend} disabled={isSubmitting}>
              {isSubmitting
                ? <span className="spinner" />
                : editingSubmissionId ? 'Save Changes' : 'Send for Review'}
            </button>
            <button className="btn btn-outline" onClick={closeCompose}>Cancel</button>
          </div>
        </div>
      )}

      {pendingSubmissions.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Pending Review</span></div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Subject / Preview</th>
                <th>Status</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingSubmissions.map(sub => {
                const meta = submissionStatusMeta[sub.status];
                const body = sub.contentData?.body ?? '';
                const editable = isPreReview(sub.status);
                return (
                  <tr key={sub.id}>
                    <td>
                      {sub.contentData?.subject && (
                        <div style={{ fontSize: 13 }}>{sub.contentData.subject}</div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                        marginTop: 2 }}>
                        {body.substring(0, 80)}{body.length > 80 ? '…' : ''}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${meta.badge}`}>{meta.label}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap' }}>
                      {sub.createdAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {editable ? (
                        <>
                          <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                            onClick={() => handleEditSubmission(sub)}>
                            Edit
                          </button>
                          <button className="btn btn-outline btn-sm"
                            onClick={() => handleCancelSubmission(sub.id)}
                            style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          Queued — can only be revoked once live
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="empty-state">No messages yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 12 }}></th>
                <th>Subject / Preview</th>
                <th>Status</th>
                <th>Sent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {messages.map(msg => {
                const unread = !isRead(msg);
                return (
                  <tr key={msg.id}>
                    <td>
                      {unread && (
                        <div style={{ width: 8, height: 8, borderRadius: 4,
                          background: 'var(--color-primary)' }} aria-label="Unread" />
                      )}
                    </td>
                    <td>
                      {msg.subject && (
                        <div style={{ fontWeight: unread ? 600 : 400, fontSize: 13 }}>
                          {msg.subject}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                        marginTop: 2 }}>
                        {msg.body.substring(0, 80)}{msg.body.length > 80 ? '…' : ''}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${msg.status === 'live' ? 'badge-green' : 'badge-gray'}`}>
                        {msg.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap' }}>
                      {msg.publishedAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                    </td>
                    <td>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => callFunction('revokeContent',
                          { contentId: msg.id, contentType: 'message', reason: 'admin_revoked' })}
                        style={{ color: 'var(--color-danger)',
                          borderColor: 'var(--color-danger)' }}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SETTINGS PAGE
// ─────────────────────────────────────────────────────────────────────────────

interface OrgAdminRequest {
  id: string;
  orgId: string;
  orgName: string;
  requesterName: string;
  requesterEmail: string;
  roleDescription: string;
  domainMismatchAcknowledged: boolean;
}

export function SettingsPage() {
  const { activeOrg, activeOrgId, signOut, user, isPlatformAdmin } = useAuth();
  const [orgColor, setOrgColor] = useState(activeOrg?.color ?? '#4a90d9');
  const [colorSaving, setColorSaving] = useState(false);
  const [orgDomain, setOrgDomain] = useState(activeOrg?.domain ?? '');
  const [domainSaving, setDomainSaving] = useState(false);

  // Pending admin requests for this org (Path B AD-10)
  const [adminRequests,        setAdminRequests]        = useState<OrgAdminRequest[]>([]);
  const [adminReqLoading,      setAdminReqLoading]      = useState(false);
  const [adminReqLoaded,       setAdminReqLoaded]       = useState(false);
  const [adminReqError,        setAdminReqError]        = useState<string | null>(null);
  const [reviewingAdminReqId,  setReviewingAdminReqId]  = useState<string | null>(null);
  const [adminReqActionError,  setAdminReqActionError]  = useState<string | null>(null);
  const [adminReqActionSuccess, setAdminReqActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (activeOrg?.color) setOrgColor(activeOrg.color);
  }, [activeOrg?.color]);

  useEffect(() => {
    if (activeOrg?.domain !== undefined) setOrgDomain(activeOrg.domain ?? '');
  }, [activeOrg?.domain]);

  const saveColor = async (color: string) => {
    if (!activeOrgId) return;
    setColorSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', activeOrgId), { color });
    } finally {
      setColorSaving(false);
    }
  };

  const saveDomain = async () => {
    if (!activeOrgId) return;
    setDomainSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', activeOrgId), {
        domain: orgDomain.trim().toLowerCase() || null,
      });
    } finally {
      setDomainSaving(false);
    }
  };

  const loadAdminRequests = useCallback(async () => {
    if (!activeOrgId) return;
    setAdminReqLoading(true);
    setAdminReqError(null);
    try {
      const result = await callFunction<{ requests: OrgAdminRequest[] }>(
        'getMyOrgAdminRequests', { orgId: activeOrgId }
      );
      setAdminRequests(result.requests ?? []);
      setAdminReqLoaded(true);
    } catch (err) {
      setAdminReqError((err as AppError).message ?? 'Failed to load requests.');
    } finally {
      setAdminReqLoading(false);
    }
  }, [activeOrgId]);

  const handleReviewAdminReq = async (requestId: string, decision: 'approved' | 'declined') => {
    setReviewingAdminReqId(requestId);
    setAdminReqActionError(null);
    setAdminReqActionSuccess(null);
    try {
      await callFunction('reviewOrgAdminRequest', { requestId, decision });
      setAdminReqActionSuccess(decision === 'approved'
        ? 'Approved — invite sent to requester.'
        : 'Request declined.');
      setAdminRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (err) {
      setAdminReqActionError((err as AppError).message ?? 'Review failed.');
    } finally {
      setReviewingAdminReqId(null);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
        Settings — {activeOrg?.name}
      </h1>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Organization</span></div>
        {[
          { label: 'Org Name', value: activeOrg?.name },
          { label: 'Org ID', value: activeOrgId ?? '—' },
          { label: 'Join Mode', value: activeOrg?.joinMode },
        ].map(row => (
          <div key={row.label}
            style={{ display: 'flex', alignItems: 'center', padding: '11px 14px',
              borderBottom: '0.5px solid var(--color-border)', gap: 10 }}>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text)' }}>
              {row.label}
            </span>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {row.value ?? '—'}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px',
          borderBottom: '0.5px solid var(--color-border)', gap: 10 }}>
          <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text)' }}>Org Color</span>
          <input type="color" value={orgColor}
            onChange={e => setOrgColor(e.target.value)}
            onBlur={e => saveColor(e.target.value)}
            style={{ width: 36, height: 28, padding: 2, border: '1px solid var(--color-border)',
              borderRadius: 4, cursor: 'pointer', background: 'none' }} />
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minWidth: 60 }}>
            {colorSaving ? 'Saving…' : orgColor}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Email Domain</span>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
              Used to flag mismatched signup requests (e.g. krueger.com)
            </div>
          </div>
          <input
            type="text"
            className="form-input"
            style={{ width: 160, fontSize: 12 }}
            value={orgDomain}
            onChange={e => setOrgDomain(e.target.value)}
            onBlur={saveDomain}
            placeholder="krueger.com"
          />
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minWidth: 50 }}>
            {domainSaving ? 'Saving…' : ''}
          </span>
        </div>
      </div>

      {activeOrg?.isWhiteLabel && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title">White Label</span>
            <span className="badge badge-blue">Premium</span>
          </div>
          {[
            { label: 'App Name', value: activeOrg.whiteLabelConfig?.branding?.appName },
            { label: 'Primary Color', value: activeOrg.whiteLabelConfig?.branding?.primaryColor },
            { label: 'Secondary Color', value: activeOrg.whiteLabelConfig?.branding?.secondaryColor },
          ].map(row => (
            <div key={row.label}
              style={{ display: 'flex', alignItems: 'center', padding: '11px 14px',
                borderBottom: '0.5px solid var(--color-border)', gap: 10 }}>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text)' }}>
                {row.label}
              </span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {row.value ?? '—'}
              </span>
              <button className="btn btn-outline btn-sm">Edit</button>
            </div>
          ))}
          <div style={{ padding: '12px 14px' }}>
            <button className="btn btn-primary btn-sm">Request New Build</button>
            <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Triggers a new App Store build with current branding
            </span>
          </div>
        </div>
      )}

      {activeOrgId && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Admins</span></div>
          <div style={{ padding: '12px 14px' }}>
            <AdminListSection
              orgId={activeOrgId}
              orgName={activeOrg?.name ?? ''}
              viewerIsPlatformAdmin={isPlatformAdmin}
              viewerUid={user?.uid ?? ''}
              showInvite={isPlatformAdmin}
            />
          </div>
        </div>
      )}

      {/* Admin access requests for this org — visible to org admins and platform admins */}
      {activeOrgId && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="card-title">Pending Admin Access Requests</span>
            <button
              className="btn btn-outline btn-sm"
              style={{ fontSize: 11 }}
              onClick={loadAdminRequests}
              disabled={adminReqLoading}
            >
              {adminReqLoading ? <span className="spinner" /> : adminReqLoaded ? 'Refresh' : 'Load'}
            </button>
          </div>
          {adminReqError && (
            <div className="error-banner" role="alert" style={{ margin: '8px 16px 0' }}>
              {adminReqError}
            </div>
          )}
          {adminReqActionError && (
            <div className="error-banner" role="alert" style={{ margin: '8px 16px 0' }}>
              {adminReqActionError}
            </div>
          )}
          {adminReqActionSuccess && (
            <div className="success-banner" role="status" style={{ margin: '8px 16px 0' }}>
              {adminReqActionSuccess}
            </div>
          )}
          {!adminReqLoaded ? (
            <div className="empty-state" style={{ fontSize: 12 }}>
              Click Load to see pending requests.
            </div>
          ) : adminRequests.length === 0 ? (
            <div className="empty-state">No pending requests.</div>
          ) : (
            adminRequests.map(req => (
              <div key={req.id}
                style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{req.requesterName}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      {req.requesterEmail}
                      {req.domainMismatchAcknowledged && (
                        <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 10 }}>
                          domain mismatch
                        </span>
                      )}
                    </div>
                    {req.roleDescription && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                        marginTop: 4, fontStyle: 'italic' }}>
                        "{req.roleDescription}"
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: 11 }}
                      disabled={reviewingAdminReqId === req.id}
                      onClick={() => handleReviewAdminReq(req.id, 'approved')}
                    >
                      {reviewingAdminReqId === req.id ? <span className="spinner" /> : 'Approve'}
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ fontSize: 11, color: 'var(--color-danger)',
                        borderColor: 'var(--color-danger)' }}
                      disabled={reviewingAdminReqId === req.id}
                      onClick={() => handleReviewAdminReq(req.id, 'declined')}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Integrations</span></div>
        <div style={{ padding: '16px 14px' }}>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
            Connect your inventory system or order API. V2 feature.
          </p>
          <button className="btn btn-outline btn-sm">+ Add Integration</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Account</span></div>
        <div style={{ padding: '12px 14px' }}>
          <button className="btn btn-outline" onClick={signOut}
            style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
